import { AMBIENT, mountAmbientBackground } from '../../engine/ambient-background';
import { icon, type IconName } from '../../engine/icons';
import { deltaVWordHtml } from '../../engine/delta-v-word';
import { claimBurst, glitchOnce } from '../../engine/glitch';
import { crystalBurst } from './crystal-burst';
import type { GameTab } from '../../engine/tabs';
import { showToast } from '../../engine/toast';
import {
  fetchStoreCatalog,
  fetchStoreBalance,
  purchaseTimeSkip,
  purchaseTitle,
  purchaseResourceDelivery,
  quoteDeltaVForCents,
  fetchOffers,
  purchaseOffer,
  type OffersResponse,
  type OfferView,
} from './shop-api';
import type { StoreCatalog, StoreBalance, TimeSkipKind, TitleSku, DeltaVPackSku, ResourceDeliverySku } from './shop-api';
import {
  checkoutPanelHtml,
  clampCents,
  formatEur,
  parseAmountToCents,
  mountCheckoutMethods,
  depositPack,
  depositCustom,
  loadPaymentMethods,
  type CheckoutState,
} from './shop-checkout';
import type { PaymentMethodPickerHandle } from '../../core/payments/widgets/payment-method-picker';
import type { PaymentMethodsResponse } from '../../core/payments/types';
import { mountWithdrawPanel } from './shop-withdraw';
import { mountHistoryPanel } from './shop-history';
import { fetchBuildings, fetchRoster, fetchTech } from '../colony/colony-api';
import { fetchTitles } from '../profile/profile-api';
import { offerCardHtml, offerName } from './offer-card';
import { formatClock } from '../../engine/boosts';
import type { BuildingType, BuildingView, RosterView, TechView, UnitType } from '../colony/colony-api';
import { t, TABLES, type Translations } from '../../engine/i18n/i18n';
import { SIDE_MENU_OPEN, mountSideMenu, sideMenuBarHtml } from '../../engine/side-menu';

/**
 * The Delta-V store: Delta-V packs, instant time-skips, resource deliveries
 * and cosmetic titles, behind a left category rail so one shelf is on screen
 * at a time rather than six stacked down an endless scroll.
 *
 * Pillar: Delta-V (premium currency) buys time, material and style, never
 * combat power
 * — see backend/src/game/store/store.catalog.ts. Every card here reads its
 * price straight from GET /store/catalog; nothing is hardcoded locally.
 *
 * Time-skip cards are the tricky part: the catalog only prices by *kind*
 * (building/training/research), but POST /store/timeskip requires a specific
 * buildingType or unitType for the first two — and store.service.ts debits
 * Delta-V BEFORE validating that anything is actually queued for that target,
 * with NO refund on a bad target. So this screen resolves the real
 * in-progress target from colony state (via colony-api, read-only) and only
 * enables Buy when one genuinely exists, re-resolving fresh immediately
 * before the purchase call (see handleBuyTimeSkip) to close the window where
 * a timer completes while the card sits on screen.
 */

const TIME_SKIP_KIND_ORDER: TimeSkipKind[] = ['building', 'training', 'research'];

const TIME_SKIP_LABEL_KEYS: Record<TimeSkipKind, keyof Translations> = {
  building: 'shop.timeSkip.building',
  training: 'shop.timeSkip.training',
  research: 'shop.timeSkip.research',
};

function timeSkipLabel(kind: TimeSkipKind): string {
  return t(TIME_SKIP_LABEL_KEYS[kind]);
}

/** Shared glossary name for a building/unit type, falling back to a humanized enum for types the glossary lacks. */
function termLabel(prefix: 'building' | 'unit', type: string): string {
  const key = `term.${prefix}.${type}` as keyof Translations;
  return key in TABLES.en ? t(key) : humanize(type);
}

const RARITY_KEYS: Record<TitleSku['rarity'], keyof Translations> = {
  COMMON: 'shop.rarity.COMMON',
  RARE: 'shop.rarity.RARE',
  EPIC: 'shop.rarity.EPIC',
  LEGENDARY: 'shop.rarity.LEGENDARY',
};

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

function formatAmount(value: number): string {
  return Math.floor(value).toLocaleString('en-US');
}

function humanize(type: string): string {
  return type
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

/** A resolved, currently-valid time-skip target — or the absence of one. */
interface TimeSkipTargetInfo {
  buildingType?: BuildingType;
  unitType?: UnitType;
  label: string;
  remainingMs: number;
}

/**
 * Picks the constructing building with the LONGEST remaining time (Builder
 * Camp lvl 5+ allows two concurrent constructions). Longest-remaining, not
 * first-found, because "first found" depends on unguaranteed server array
 * order, and the target most likely to still be valid by the time the
 * purchase round-trip lands is the one furthest from completing.
 */
function resolveBuildingTarget(buildings: BuildingView[]): TimeSkipTargetInfo | null {
  const constructing = buildings.filter((b) => b.constructing && b.constructionEndAt);
  if (constructing.length === 0) return null;
  const target = constructing.reduce((longest, b) =>
    new Date(b.constructionEndAt!).getTime() > new Date(longest.constructionEndAt!).getTime() ? b : longest,
  );
  return {
    buildingType: target.type,
    label: termLabel('building', target.type),
    remainingMs: new Date(target.constructionEndAt!).getTime() - Date.now(),
  };
}

/** Same longest-remaining tie-break as resolveBuildingTarget (Barracks lvl 5+ allows two training slots). */
function resolveUnitTarget(roster: RosterView[]): TimeSkipTargetInfo | null {
  const training = roster.filter((u) => u.training && u.trainEndAt);
  if (training.length === 0) return null;
  const target = training.reduce((longest, u) =>
    new Date(u.trainEndAt!).getTime() > new Date(longest.trainEndAt!).getTime() ? u : longest,
  );
  return {
    unitType: target.unitType,
    label: termLabel('unit', target.unitType),
    remainingMs: new Date(target.trainEndAt!).getTime() - Date.now(),
  };
}

/** Research single-threads the whole tech tree — at most one in progress, no target to disambiguate. */
function resolveResearchTarget(tech: TechView | null): TimeSkipTargetInfo | null {
  if (!tech?.researching || !tech.researchEndAt) return null;
  return { label: t('shop.timeSkip.techResearch'), remainingMs: new Date(tech.researchEndAt).getTime() - Date.now() };
}

export function renderShopScreen(root: HTMLElement): () => void {
  root.innerHTML = `
    <div class="relative w-full h-full">
      <div data-ambient class="absolute inset-0 overflow-hidden pointer-events-none"></div>
      <div id="shop-screen" class="relative w-full h-full flex flex-col">
        <div class="text-center text-text-dim py-10">${t('shop.loading')}</div>
      </div>
    </div>
  `;
  mountAmbientBackground(root.querySelector('[data-ambient]') as HTMLElement, AMBIENT.shop);
  const container = root.querySelector('#shop-screen') as HTMLDivElement;

  let disposed = false;

  let catalog: StoreCatalog | null = null;
  /** Titles the player already owns (earned or bought) — from the profile API, the one source of ownership. */
  let ownedTitleIds = new Set<string>();
  let balance: StoreBalance | null = null;
  /** Today's rotating offers (null until loaded; empty if the endpoint failed). */
  let offersData: OffersResponse | null = null;
  /** Wall-clock offset to the server's `now`, so the reset timer is right even on a skewed clock. */
  let serverSkewMs = 0;
  let offersSeen = false;
  let buildings: BuildingView[] = [];
  let roster: RosterView[] = [];
  let tech: TechView | null = null;

  /**
   * Shared in-flight guard across all three purchase handlers. Without it, a
   * double-tap (or two fast clicks before the disabled-state re-render lands)
   * dispatches two concurrent handler runs that both see the same
   * pre-purchase target/balance and both POST — for time-skips specifically,
   * the second POST debits Delta-V for a target the first call already
   * completed, and store.service.ts does NOT refund that (see its
   * purchaseTimeSkip doc comment). Checked synchronously before the first
   * `await` in each handler, so it closes the race even for two clicks
   * handled back-to-back in the same tick.
   */
  let purchasing = false;

  /** Which shelf the rail is showing. Delta-V first: it is the only category that brings currency IN, so an empty wallet starts where it can be filled. */
  let activeCategory: CategoryId = 'delta-v';

  /** Custom-amount checkout state. Seeded from the server's lower bound once the catalog lands. */
  let checkout: CheckoutState = { cents: 0, currency: 'EUR' };

  /** Core payment methods (loaded once). */
  let paymentMethods: PaymentMethodsResponse | null = null;
  let methodPickerHandle: PaymentMethodPickerHandle | null = null;
  let panelHandle: { destroy(): void } | null = null;

  function targetFor(kind: TimeSkipKind): TimeSkipTargetInfo | null {
    if (kind === 'building') return resolveBuildingTarget(buildings);
    if (kind === 'training') return resolveUnitTarget(roster);
    return resolveResearchTarget(tech);
  }

  // ------------------------------------------------------------------ cards

  /** Generated item art (public/shop): a full-colour object per item, no background. It glitches now and then (CSS `shop-ico`). */
  const TIME_SKIP_ICON: Record<TimeSkipKind, string> = {
    building: '/shop/skip-building.webp',
    training: '/shop/skip-training.webp',
    research: '/shop/skip-research.webp',
  };
  const RESOURCE_ICON_IMG: Partial<Record<ResourceDeliverySku['resourceType'], string>> = {
    WATER_ICE: '/shop/res-water.webp',
    REGOLITH: '/shop/res-regolith.webp',
    HE3: '/shop/res-he3.webp',
  };
  const DELTA_V_PACK_ICON: Record<string, string> = {
    dv_starter: '/shop/dv-starter.webp',
    dv_supply: '/shop/dv-supply.webp',
    dv_explorer: '/shop/dv-explorer.webp',
    dv_commander: '/shop/dv-commander.webp',
    dv_pioneer: '/shop/dv-pioneer.webp',
  };

  function itemIconHtml(src: string | undefined, size: 'md' | 'lg' = 'md'): string {
    return src ? `<img class="shop-ico shop-ico--${size}" src="${src}" alt="" aria-hidden="true" loading="lazy" draggable="false" />` : '';
  }

  /** The purchase celebration, as for a claimed quest: the buy button tears, then the whole card plays the claim glitch. Resolves once it has played (immediately under reduced motion / in jsdom). */
  async function celebratePurchase(cardSelector: string): Promise<void> {
    const card = container.querySelector<HTMLElement>(cardSelector);
    if (!card) return;
    const button = card.querySelector<HTMLElement>('[data-action^="buy-"]');
    if (button) glitchOnce(button, 'hard');
    // Crimson Dust cards also sprout a burst of crystals around them, which grow, hold and vanish.
    if (card.classList.contains('crystal-wrap')) crystalBurst(card);
    await claimBurst(card);
  }

  function buyControlHtml(action: string, dataAttrs: string, price: number, disabled: boolean, note: string): string {
    return `
      <div class="flex items-center justify-between gap-2 mt-1">
        <span class="text-xs text-gold tabular-nums flex items-baseline gap-1"><span aria-hidden="true" class="font-bold">${formatAmount(price)}</span>${deltaVWordHtml()}</span>
        <button type="button" data-action="${action}" ${dataAttrs} ${disabled ? 'disabled' : ''}
          class="font-button shop-buy min-h-12 px-6 bg-gold text-bg text-base font-bold rounded transition-colors duration-[120ms] hover:bg-gold-bright disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed touch-manipulation">
          ${t('shop.buy')}
        </button>
      </div>
      ${note ? `<p class="text-[10px] text-crimson leading-snug mt-1">${note}</p>` : ''}
    `;
  }

  function timeSkipCardHtml(kind: TimeSkipKind, price: number): string {
    const target = targetFor(kind);
    const premium = balance?.premium ?? 0;
    const noTarget = !target;
    const insufficient = !noTarget && premium < price;
    const disabled = noTarget || insufficient;
    const note = noTarget ? t('shop.timeSkip.nothingToSkip') : insufficient ? t('shop.notEnoughDeltaV') : '';
    const context = target
      ? escapeHtml(t('shop.timeSkip.context', { label: target.label, time: formatCountdown(target.remainingMs) }))
      : t('shop.timeSkip.nothingQueued');
    return `
      <div class="glass-panel px-3 py-3 flex items-center gap-3" data-card="timeskip-${kind}">
        ${itemIconHtml(TIME_SKIP_ICON[kind], 'lg')}
        <div class="flex flex-col flex-1 min-w-0">
          <div class="text-sm text-text font-bold">${timeSkipLabel(kind)}</div>
          <div class="text-[10px] text-text-dim leading-relaxed mt-0.5" data-card-context>${context}</div>
          ${buyControlHtml('buy-timeskip', `data-kind="${kind}"`, price, disabled, note)}
        </div>
      </div>
    `;
  }

  /**
   * Tint per rarity band, mirroring the profile card's `title-plate`, so a
   * title looks the same here as it will once worn.
   */
  const TITLE_RARITY_TINT: Record<TitleSku['rarity'], string> = {
    COMMON: 'var(--color-text-dim)',
    RARE: 'var(--color-purple-light)',
    EPIC: 'var(--color-gold)',
    LEGENDARY: 'var(--color-gold-bright)',
  };

  /**
   * The grant is a PlayerTitle row, not an inventory item — see
   * StoreService.purchaseTitle.
   *
   * One inline row, not a stacked card: at two columns on a 320px screen
   * there is only ~124px of content width per card, so the separate price
   * readout from `buyControlHtml` (dot + name + price glyph + a literal
   * "Buy" label would not fit) is folded into the button face instead,
   * mirroring `deltaVPackCardHtml` below — that card already shows its
   * price ON the button rather than beside a generic label. The disabled
   * "Not enough Delta-V" reason moves from a visible note line (no room for
   * one here) to the button's `aria-label`, since a `title` attribute is
   * unreliable on a `disabled` control (suppressed pointer events in some
   * browsers, and a disabled button is out of tab order for screen readers
   * either way).
   */
  function titleCardHtml(sku: TitleSku): string {
    if (ownedTitleIds.has(sku.id)) {
      return `
      <div class="glass-panel px-2 py-2 flex items-center gap-1.5 opacity-90" data-card="title-${sku.id}" data-owned="true">
        <span class="w-1.5 h-1.5 rounded-full shrink-0" style="background:${TITLE_RARITY_TINT[sku.rarity]}" aria-hidden="true"></span>
        <span class="text-xs text-text truncate flex-1 min-w-0" title="${escapeHtml(sku.name)}">${escapeHtml(sku.name)}</span>
        <span class="shrink-0 min-h-11 px-2 flex items-center text-[11px] font-bold uppercase tracking-wider text-success border border-success/40 rounded">${t('shop.titles.owned')}</span>
      </div>`;
    }
    const premium = balance?.premium ?? 0;
    const disabled = premium < sku.price;
    const buyLabel = disabled
      ? t('shop.title.buyDisabled', { name: sku.name })
      : t('shop.title.buy', { name: sku.name, price: formatAmount(sku.price) });
    return `
      <div class="glass-panel px-2 py-2 flex items-center gap-1.5" data-card="title-${sku.id}">
        <span class="w-1.5 h-1.5 rounded-full shrink-0" style="background:${TITLE_RARITY_TINT[sku.rarity]}" aria-hidden="true"></span>
        <span class="text-xs text-text truncate flex-1 min-w-0" title="${escapeHtml(sku.name)}">${escapeHtml(sku.name)}</span>
        <button type="button" data-action="buy-title" data-sku="${sku.id}" ${disabled ? 'disabled' : ''}
          aria-label="${escapeHtml(buyLabel)}"
          class="shrink-0 flex items-center gap-1 font-button shop-buy min-h-12 px-3 bg-gold text-bg text-sm font-bold rounded transition-colors duration-[120ms] hover:bg-gold-bright disabled:bg-border disabled:text-text-dim disabled:cursor-not-allowed touch-manipulation">
          <span aria-hidden="true" class="flex items-baseline gap-1">${formatAmount(sku.price)}${deltaVWordHtml()}</span>
        </button>
      </div>
    `;
  }

  /** Titles grouped by rarity, cheapest band first — 51 flat cards would be unreadable. */
  function titlesSectionHtml(titles: TitleSku[]): string {
    const bands: TitleSku['rarity'][] = ['COMMON', 'RARE', 'EPIC', 'LEGENDARY'];
    const groups = bands
      .map((rarity) => {
        const rows = titles.filter((sku) => sku.rarity === rarity);
        if (rows.length === 0) return '';
        return `
          <div class="flex flex-col gap-2">
            <div class="flex items-baseline justify-between gap-2">
              <h3 class="font-heading text-[11px] text-text-dim uppercase tracking-[0.18em]">${t(RARITY_KEYS[rarity])}</h3>
              <span class="text-[10px] text-text-dim tabular-nums">${rows.length}</span>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">${rows.map(titleCardHtml).join('')}</div>
          </div>`;
      })
      .join('');

    return `
      <section>
        <h2 class="font-heading text-xs text-gold mb-1 tracking-wide uppercase">${t('shop.titles.heading')}</h2>
        <p class="text-xs text-text-dim leading-relaxed mb-2">${t('shop.titles.intro')}</p>
        <div class="flex flex-col gap-4" data-section="titles">${groups}</div>
      </section>
    `;
  }

  /**
   * Unlike every other card here, this one GRANTS Delta-V rather than
   * spending it — so there's no "not enough Delta-V" disabled state, and the
   * price shown is real-money (EUR), not Delta-V. Always enabled: a real
   * payment flow has nothing analogous to "insufficient balance" blocking
   * the button client-side (any failure is surfaced from the purchase call
   * itself, same as every other card's error path).
   */
  /**
   * `spanClass` places the card in the asymmetric 3-then-2 grid below. The
   * card itself has no opinion about its width — it fills whatever track it
   * is given, so the layout lives entirely in one place.
   */
  function deltaVPackCardHtml(sku: DeltaVPackSku, spanClass = ''): string {
    const priceLabel = `€${(sku.priceCents / 100).toFixed(2)}`;
    const perEuro = Math.round(sku.deltaV / (sku.priceCents / 100));
    return `
      <div class="${spanClass} glass-panel px-4 py-5 flex flex-col items-center text-center gap-1 relative overflow-hidden border-2 border-gold/20 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(212,175,55,0.2)] hover:border-gold/40 group cursor-pointer" data-card="delta-v-${sku.id}">
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500 pointer-events-none"></div>
        ${itemIconHtml(DELTA_V_PACK_ICON[sku.id], 'lg') || `<span class="text-gold">${deltaVWordHtml('lg')}</span>`}
        <div class="font-heading text-2xl text-gold tabular-nums mt-1">${formatAmount(sku.deltaV)} ${deltaVWordHtml('md')}</div>
        <div class="text-[10px] text-text-dim uppercase tracking-wide">${escapeHtml(sku.label)}</div>
        <div class="text-[10px] text-text-dim tabular-nums">${t('shop.pack.perEuro', { amount: perEuro })}</div>
        <button type="button" data-action="buy-delta-v" data-pack="${sku.id}"
          class="font-button shop-buy w-full min-h-12 px-4 mt-3 bg-gold text-bg text-base font-bold rounded transition-colors duration-[120ms] hover:bg-gold-bright touch-manipulation">
          ${priceLabel}
        </button>
      </div>
    `;
  }

  function offerCards(filter: (o: OfferView) => boolean, variant: 'tile' | 'row' = 'tile'): string {
    const list = (offersData?.offers ?? []).filter(filter);
    const now = Date.now() - serverSkewMs;
    return list.map((o) => offerCardHtml(o, { balance: balance?.premium ?? 0, now, variant, escape: escapeHtml, formatAmount })).join('');
  }

  function offersResetHtml(): string {
    if (!offersData) return '';
    const left = new Date(offersData.windowEndsAt).getTime() - (Date.now() - serverSkewMs);
    return `<div class="offers-timer" role="timer"><span class="offers-timer-dot" aria-hidden="true"></span><span data-offers-reset>${t('offers.resetsIn', { time: formatClock(left) })}</span></div>`;
  }

  const RESOURCE_DELIVERY_ICONS: Record<ResourceDeliverySku['resourceType'], IconName> = {
    REGOLITH: 'regolith',
    WATER_ICE: 'waterIce',
    HE3: 'gem',
    CRIMSON_DUST: 'star',
  };

  /** Spends Delta-V on raw material — a separate purchase group from time and style, but the same disabled-when-short-of-balance shape. */
  function resourceDeliveryCardHtml(sku: ResourceDeliverySku): string {
    const premium = balance?.premium ?? 0;
    const disabled = premium < sku.price;
    const note = disabled ? t('shop.notEnoughDeltaV') : '';
    const body = `
        <div class="text-sm text-text flex items-center gap-1.5">${icon(RESOURCE_DELIVERY_ICONS[sku.resourceType])}<span>${escapeHtml(sku.label)}</span></div>
        <div class="text-xs text-text-dim tabular-nums mt-0.5">+${formatAmount(sku.amount)}</div>
        ${buyControlHtml('buy-resource', `data-sku="${sku.id}"`, sku.price, disabled, note)}`;
    if (sku.resourceType !== 'CRIMSON_DUST') {
      return `<div class="glass-panel px-3 py-3 flex items-center gap-3" data-card="resource-${sku.id}">${itemIconHtml(RESOURCE_ICON_IMG[sku.resourceType], 'lg')}<div class="flex flex-col flex-1 min-w-0">${body}</div></div>`;
    }
    // Crimson Dust is the crystal: faceted frame, ruby shards rising behind the text, a slow glint. The bigger pack
    // grows a bigger cluster. The faceted card clips its own corners, so the crystals that grow AROUND it live on an
    // unclipped wrapper (`data-card` sits on the wrapper; a purchase sprouts more of them — see celebratePurchase).
    const big = sku.amount >= 300;
    return `
      <div class="crystal-wrap" data-card="resource-${sku.id}">
        <div class="crystal-card${big ? ' crystal-card--big' : ''} px-3 py-3 flex flex-col">
          <img class="cc-cry cc-cry--a" src="/crystals/crystal-2.webp" alt="" draggable="false">
          <img class="cc-cry cc-cry--b" src="/crystals/crystal-1.webp" alt="" draggable="false">
          ${big ? '<img class="cc-cry cc-cry--c" src="/crystals/crystal-3.webp" alt="" draggable="false">' : '<img class="cc-cry cc-cry--c" src="/crystals/crystal-4.webp" alt="" draggable="false">'}
          <span class="cc-glint" aria-hidden="true"></span>
          <div class="cc-body flex flex-col">${body}</div>
        </div>
        <img class="cc-edge cc-edge--bl" src="/crystals/crystal-4.webp" alt="" aria-hidden="true" draggable="false">
        <img class="cc-edge cc-edge--tl" src="/crystals/crystal-1.webp" alt="" aria-hidden="true" draggable="false">
        <img class="cc-edge cc-edge--tr" src="/crystals/crystal-2.webp" alt="" aria-hidden="true" draggable="false">
        ${big ? '<img class="cc-edge cc-edge--br" src="/crystals/crystal-3.webp" alt="" aria-hidden="true" draggable="false">' : '<img class="cc-edge cc-edge--br" src="/crystals/crystal-5.webp" alt="" aria-hidden="true" draggable="false">'}
      </div>`;
  }

  // ------------------------------------------------------------------ render

  // ------------------------------------------------------------- categories

  /**
   * The rail. One shelf is on screen at a time; the previous shop stacked six
   * sections down a single scroll, which buried Titles (51 cards) under
   * everything else and made the premium packs — the only thing that brings
   * Delta-V in — just another row.
   */
  const CATEGORIES = [
    { id: 'offers', labelKey: 'shop.category.offers' },
    { id: 'delta-v', labelKey: 'shop.category.deltaV' },
    { id: 'skip-time', labelKey: 'shop.category.skipTime' },
    { id: 'resources', labelKey: 'shop.category.resources' },
    { id: 'titles', labelKey: 'shop.category.titles' },
    { id: 'withdraw', labelKey: 'shop.category.withdraw' },
    { id: 'history', labelKey: 'shop.category.history' },
  ] as const;
  type CategoryId = (typeof CATEGORIES)[number]['id'];

  function railHtml(): string {
    return CATEGORIES.map(
      (c) => `
        <button type="button" data-action="pick-category" data-category="${c.id}"
          aria-current="${c.id === activeCategory ? 'page' : 'false'}"
          class="w-full text-left min-h-11 px-3 py-2 border-l-[3px] text-[11px] uppercase tracking-[0.14em] transition-colors duration-[120ms] touch-manipulation ${
            c.id === activeCategory
              ? 'border-l-gold bg-surface-2 text-text'
              : 'border-l-transparent text-text-dim hover:text-text'
          }">${t(c.labelKey)}</button>`,
    ).join('');
  }

  function shelfHtml(): string {
    if (!catalog) return '';
    switch (activeCategory) {
      case 'offers': {
        const cards = offerCards(() => true);
        return `
          <div class="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <h2 class="font-heading text-xs text-gold tracking-wide uppercase">${t('offers.heading')}</h2>
              <p class="text-xs text-text-dim leading-relaxed">${t('offers.intro')}</p>
            </div>
            ${offersResetHtml()}
          </div>
          ${cards ? `<div class="grid grid-cols-1 sm:grid-cols-2 gap-3" data-section="offers">${cards}</div>` : `<p class="text-xs text-text-dim">${t('offers.empty')}</p>`}
        `;
      }
      case 'delta-v':
        return `
          <p class="text-xs text-text-dim leading-relaxed">${t('shop.deltaV.intro')}</p>
          <!-- Asymmetric 3-then-2 layout over a 6-column track: the first
               three cards span 2 each and the last two span 3 each, so BOTH
               rows end flush at the full width rather than leaving a gap
               where a sixth pack would have gone. Mobile keeps two columns,
               with the odd last card widened to fill its row. -->
          <div class="grid grid-cols-2 sm:grid-cols-6 gap-3" data-section="delta-v">
            ${catalog.deltaVPacks
              .map((sku, i, all) => {
                const desktop = i < 3 ? 'sm:col-span-2' : 'sm:col-span-3';
                const mobile = i === all.length - 1 && all.length % 2 === 1 ? 'col-span-2' : '';
                return deltaVPackCardHtml(sku, `${mobile} ${desktop}`.trim());
              })
              .join('')}
          </div>
          ${checkoutPanelHtml({
            state: checkout,
            packs: catalog.deltaVPacks,
            minCents: catalog.deltaVCustomMinCents,
            maxCents: catalog.deltaVCustomMaxCents,
            busy: purchasing,
            methods: paymentMethods?.deposit ?? null,
          }) ?? ''}
        `;
      case 'skip-time': {
        const kinds = TIME_SKIP_KIND_ORDER.filter((k) => k in catalog!.timeSkipPrices);
        return `
          ${offerCards((o) => o.page === 'skip-time', 'row') ? `<div class="flex flex-col gap-3" data-section="skip-offers">${offerCards((o) => o.page === 'skip-time', 'row')}</div>` : ''}
          <p class="text-xs text-text-dim leading-relaxed">${t('shop.timeSkip.intro')}</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3" data-section="timeskips">
            ${kinds.map((k) => timeSkipCardHtml(k, catalog!.timeSkipPrices[k])).join('')}
          </div>`;
      }
      case 'resources':
        return `
          ${offerCards((o) => o.page === 'resources') ? `<div class="grid grid-cols-1 sm:grid-cols-3 gap-3" data-section="resource-offers">${offerCards((o) => o.page === 'resources')}</div>` : ''}
          <p class="text-xs text-text-dim leading-relaxed">${t('shop.resources.intro')}</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3" data-section="resource-delivery">
            ${catalog.resourceDeliveries.map((sku) => resourceDeliveryCardHtml(sku)).join('')}
          </div>`;
      case 'titles':
        return (offerCards((o) => o.page === 'titles') ? `<div data-section="title-offers">${offerCards((o) => o.page === 'titles')}</div>` : '') + titlesSectionHtml(catalog.titles ?? []);
      case 'withdraw':
        return '<div data-mount="withdraw"></div>';
      case 'history':
        return '<div data-mount="history"></div>';
    }
  }

  function render(): void {
    if (!catalog || !balance) return;
    container.innerHTML = `
      <div class="w-full h-full flex flex-col">
        <!-- The balance lives in the header's Delta-V button; this is the screen-reader/test hook only. -->
        <span class="sr-only" data-balance-premium>${formatAmount(balance.premium)}</span>

        <div class="flex-1 min-h-0 overflow-y-auto">
          <div class="p-4 sm:p-6 max-w-5xl mx-auto flex flex-col sm:flex-row gap-4 sm:gap-6 pb-10">
            ${SIDE_MENU_OPEN}
              ${sideMenuBarHtml(t(CATEGORIES.find((c) => c.id === activeCategory)!.labelKey), t('shop.categoriesAria'), 'shop-menu')}
              <nav id="shop-menu" aria-label="${t('shop.categoriesAria')}"
                class="side-menu-panel holo-rail shrink-0 sm:w-40 flex sm:flex-col gap-1 sm:overflow-visible sm:sticky sm:top-0 sm:self-start">
                ${railHtml()}
              </nav>
            </div>
            <div class="flex-1 min-w-0 flex flex-col gap-3" data-shelf>${shelfHtml()}</div>
          </div>
        </div>
      </div>
    `;
    mountMethodPicker();
    mountPanel();
  }

  function mountPanel(): void {
    panelHandle?.destroy();
    panelHandle = null;
    if (activeCategory === 'withdraw') {
      const el = container.querySelector('[data-mount="withdraw"]') as HTMLElement | null;
      if (el) {
        panelHandle = mountWithdrawPanel({
          root: el,
          onError: (err) => showToast(err.message, 'error'),
          onSubmitted: () => {
            showToast(t('shop.toast.withdrawSubmitted'), 'success');
          },
        });
      }
    } else if (activeCategory === 'history') {
      const el = container.querySelector('[data-mount="history"]') as HTMLElement | null;
      if (el) {
        panelHandle = mountHistoryPanel({
          root: el,
          onError: (err) => showToast(err.message, 'error'),
        });
      }
    }
  }

  function mountMethodPicker(): void {
    methodPickerHandle?.destroy();
    methodPickerHandle = null;
    if (activeCategory !== 'delta-v' || !paymentMethods) return;
    const enabled = paymentMethods.deposit.filter((m) => m.enabled);
    if (enabled.length === 0) return;
    methodPickerHandle = mountCheckoutMethods(
      container,
      enabled,
      checkout.currency || paymentMethods.defaultCurrency,
    );
  }

  // ------------------------------------------------------------------- data

  /**
   * Buildings/roster/tech are used only to resolve time-skip targets — a
   * colonyless account (or any transient failure) must not break XP boosts
   * or cosmetics, which need no colony at all. Any failure degrades to "no
   * target" (never a stale one) rather than surfacing an error.
   */
  async function refreshColonyState(): Promise<void> {
    const [b, r, te] = await Promise.allSettled([fetchBuildings(), fetchRoster(), fetchTech()]);
    if (disposed) return;
    buildings = b.status === 'fulfilled' ? b.value : [];
    roster = r.status === 'fulfilled' ? r.value : [];
    tech = te.status === 'fulfilled' ? te.value : null;
  }

  async function refreshBalance(): Promise<void> {
    try {
      balance = await fetchStoreBalance();
    } catch {
      // Keep the last-known balance rather than blanking it.
    }
  }

  async function loadAll(): Promise<void> {
    try {
      const [c, bal, methods] = await Promise.all([fetchStoreCatalog(), fetchStoreBalance(), loadPaymentMethods()]);
      if (disposed) return;
      catalog = c;
      balance = bal;
      paymentMethods = methods;
      if (methods) checkout.currency = methods.defaultCurrency;
      const midPack = [...c.deltaVPacks].sort((a, b) => a.priceCents - b.priceCents)[Math.floor(c.deltaVPacks.length / 2)];
      checkout = { ...checkout, cents: clampCents(midPack?.priceCents ?? c.deltaVCustomMinCents, c.deltaVCustomMinCents, c.deltaVCustomMaxCents) };
    } catch (err) {
      if (disposed) return;
      const message = err instanceof Error ? err.message : t('shop.error.loadShop');
      container.innerHTML = `<div class="text-center text-error py-10">${escapeHtml(message)}</div>`;
      showToast(message, 'error');
      return;
    }
    await refreshOwnedTitles();
    await refreshOffers();
    await refreshColonyState();
    if (disposed) return;
    render();
  }

  async function refreshOffers(): Promise<void> {
    try {
      const res = await fetchOffers();
      if (!res || !Array.isArray(res.offers)) return;
      offersData = res;
      serverSkewMs = Date.now() - new Date(res.now).getTime();
      // First load: start on the offers page when there is anything to show.
      if (!offersSeen && res.offers.length > 0) activeCategory = 'offers';
      offersSeen = true;
    } catch {
      // No offers is a fine state: the rest of the shop works without them.
    }
  }

  async function refreshOwnedTitles(): Promise<void> {
    try {
      ownedTitleIds = new Set((await fetchTitles()).filter((x) => x.owned).map((x) => x.id));
    } catch {
      // Without ownership every card simply stays buyable; the server still refuses a duplicate.
    }
  }

  // ---------------------------------------------------------------- actions

  async function handleBuyTimeSkip(kind: TimeSkipKind): Promise<void> {
    if (purchasing) return;
    purchasing = true;
    try {
      // Re-resolve target AND balance fresh, immediately before spending: the
      // debit is NOT refunded if the target has completed since the card was
      // drawn (see shop-api.ts's purchaseTimeSkip doc comment), and the
      // on-screen balance may be stale if Delta-V was spent elsewhere (another
      // tab, or a time-skip purchase just above this one in the same click).
      await Promise.all([refreshColonyState(), refreshBalance()]);
      if (disposed) return;
      const target = targetFor(kind);
      if (!target) {
        showToast(t('shop.timeSkip.nothingToSkipNow'), 'error');
        render();
        return;
      }
      const price = catalog?.timeSkipPrices[kind] ?? 0;
      if ((balance?.premium ?? 0) < price) {
        showToast(t('shop.notEnoughDeltaV'), 'error');
        render();
        return;
      }
      try {
        await purchaseTimeSkip(kind, { buildingType: target.buildingType, unitType: target.unitType });
        if (disposed) return;
        showToast(t('shop.timeSkip.done', { label: timeSkipLabel(kind) }), 'success');
        await celebratePurchase(`[data-card="timeskip-${kind}"]`);
        if (disposed) return;
        await Promise.all([refreshBalance(), refreshColonyState()]);
        if (disposed) return;
        render();
      } catch (err) {
        if (disposed) return;
        showToast(err instanceof Error ? err.message : t('shop.timeSkip.failed'), 'error');
      }
    } finally {
      purchasing = false;
    }
  }

  async function handleBuyOffer(offerId: string): Promise<void> {
    if (purchasing) return;
    const offer = offersData?.offers.find((o) => o.id === offerId);
    if (!offer) return;
    purchasing = true;
    try {
      const res = await purchaseOffer(offerId);
      if (disposed) return;
      if (offer.kind === 'TITLE' && res.grantedTitleId) {
        const sku = catalog?.titles.find((x) => x.id === res.grantedTitleId);
        ownedTitleIds.add(res.grantedTitleId);
        showToast(t('offers.toast.title', { title: sku?.name ?? res.grantedTitleId }), 'success');
      } else if (offer.pack) {
        showToast(t('offers.toast.pack', { amount: formatAmount(offer.pack.amount), resource: offerName(offer) }), 'success');
      } else {
        showToast(t('offers.toast.bought', { name: offerName(offer) }), 'success');
      }
      window.dispatchEvent(new Event('boosts:refresh'));
      window.dispatchEvent(new Event('deltav:refresh'));
      await celebratePurchase(`[data-card="offer-${offerId}"]`);
      if (disposed) return;
      await Promise.all([refreshBalance(), refreshOffers()]);
      if (disposed) return;
      render();
    } catch (err) {
      if (disposed) return;
      showToast(err instanceof Error ? err.message : t('offers.toast.failed'), 'error');
    } finally {
      purchasing = false;
    }
  }

  async function handleBuyTitle(skuId: string, label: string): Promise<void> {
    if (purchasing) return;
    purchasing = true;
    try {
      await purchaseTitle(skuId);
      if (disposed) return;
      ownedTitleIds.add(skuId);
      showToast(t('shop.titles.unlocked', { label }), 'success');
      await celebratePurchase(`[data-card="title-${skuId}"]`);
      if (disposed) return;
      await refreshBalance();
      if (disposed) return;
      render();
    } catch (err) {
      if (disposed) return;
      showToast(err instanceof Error ? err.message : t('shop.titles.failed'), 'error');
    } finally {
      purchasing = false;
    }
  }

  function getSelectedMethod(): string | null {
    return methodPickerHandle?.getSelected()?.id ?? paymentMethods?.deposit.find((m) => m.enabled)?.id ?? null;
  }

  function getSelectedCurrency(): string {
    return methodPickerHandle?.getCurrency() ?? (checkout.currency || 'EUR');
  }

  async function handleBuyDeltaV(packId: string): Promise<void> {
    if (purchasing) return;
    const method = getSelectedMethod();
    if (!method) { showToast(t('shop.error.selectMethod'), 'error'); return; }
    purchasing = true;
    try {
      await depositPack(packId, method as any, getSelectedCurrency());
    } catch (err) {
      if (disposed) return;
      showToast(err instanceof Error ? err.message : t('shop.error.startPayment'), 'error');
    } finally {
      purchasing = false;
    }
  }

  async function handleBuyResourceDelivery(skuId: string): Promise<void> {
    if (purchasing) return;
    purchasing = true;
    try {
      const sku = catalog?.resourceDeliveries.find((s) => s.id === skuId);
      await purchaseResourceDelivery(skuId);
      if (disposed) return;
      showToast(
        t('shop.resources.onTheWay', {
          item: sku ? `+${formatAmount(sku.amount)} ${sku.label}` : t('shop.resources.deliveryFallback'),
        }),
        'success',
      );
      await celebratePurchase(`[data-card="resource-${skuId}"]`);
      if (disposed) return;
      await refreshBalance();
      if (disposed) return;
      render();
    } catch (err) {
      if (disposed) return;
      showToast(err instanceof Error ? err.message : t('shop.resources.failed'), 'error');
    } finally {
      purchasing = false;
    }
  }

  async function handleBuyCustomDeltaV(): Promise<void> {
    if (purchasing || !catalog) return;
    const method = getSelectedMethod();
    if (!method) { showToast(t('shop.error.selectMethod'), 'error'); return; }
    purchasing = true;
    try {
      const cents = clampCents(checkout.cents, catalog.deltaVCustomMinCents, catalog.deltaVCustomMaxCents);
      await depositCustom(cents, method as any, getSelectedCurrency());
    } catch (err) {
      if (disposed) return;
      showToast(err instanceof Error ? err.message : t('shop.error.startPayment'), 'error');
    } finally {
      purchasing = false;
    }
  }

  /**
   * Slider and typed amount, patched in place rather than through render().
   *
   * A full repaint on every drag frame would replace the <input> the pointer
   * is currently holding, which drops the drag on the first move. Only the two
   * read-outs are rewritten, and the field that currently has focus is left
   * alone so a half-typed figure is never overwritten mid-keystroke.
   */
  function paintCheckoutReadouts(): void {
    if (!catalog) return;
    const cents = clampCents(checkout.cents, catalog.deltaVCustomMinCents, catalog.deltaVCustomMaxCents);
    const deltaV = quoteDeltaVForCents(cents, catalog.deltaVPacks, catalog.deltaVCustomMinCents, catalog.deltaVCustomMaxCents);

    const dv = container.querySelector('[data-checkout-deltav]');
    if (dv) dv.textContent = deltaV.toLocaleString('en-US');
    const price = container.querySelector('[data-checkout-price]');
    if (price) price.textContent = formatEur(cents);

    const slider = container.querySelector('[data-checkout-slider]') as HTMLInputElement | null;
    if (slider && document.activeElement !== slider) slider.value = String(cents);
    const amount = container.querySelector('[data-checkout-amount]') as HTMLInputElement | null;
    if (amount && document.activeElement !== amount) amount.value = (cents / 100).toFixed(2);
  }

  function onInput(e: Event): void {
    const el = e.target as HTMLElement;
    if (!catalog) return;
    if (el.matches('[data-checkout-slider]')) {
      checkout = { ...checkout, cents: Number((el as HTMLInputElement).value) };
      paintCheckoutReadouts();
    } else if (el.matches('[data-checkout-amount]')) {
      // Loose while typing: an unparseable value leaves the last good amount in
      // place instead of snapping the field to zero mid-keystroke.
      const parsed = parseAmountToCents((el as HTMLInputElement).value);
      if (parsed === null) return;
      checkout = { ...checkout, cents: parsed };
      paintCheckoutReadouts();
    }
  }

  /** Commits the typed amount on blur: this is where clamping is allowed to move what the player typed. */
  function onFocusOut(e: FocusEvent): void {
    const el = e.target as HTMLElement;
    if (!catalog || !el.matches('[data-checkout-amount]')) return;
    checkout = {
      ...checkout,
      cents: clampCents(checkout.cents, catalog.deltaVCustomMinCents, catalog.deltaVCustomMaxCents),
    };
    paintCheckoutReadouts();
  }

  function onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const actionEl = target.closest('[data-action]') as HTMLElement | null;
    if (!actionEl) return;
    const action = actionEl.dataset.action;
    if (action === 'pick-category') {
      activeCategory = actionEl.dataset.category as CategoryId;
      render();
    } else if (action === 'buy-custom-delta-v') {
      void handleBuyCustomDeltaV();
    } else if (action === 'buy-timeskip') {
      void handleBuyTimeSkip(actionEl.dataset.kind as TimeSkipKind);
    } else if (action === 'buy-offer') {
      void handleBuyOffer(actionEl.dataset.offer!);
    } else if (action === 'buy-title') {
      const sku = catalog?.titles.find((title) => title.id === actionEl.dataset.sku);
      void handleBuyTitle(actionEl.dataset.sku!, sku?.name ?? t('shop.titles.fallbackName'));
    } else if (action === 'buy-delta-v') {
      void handleBuyDeltaV(actionEl.dataset.pack!);
    } else if (action === 'buy-resource') {
      void handleBuyResourceDelivery(actionEl.dataset.sku!);
    }
  }
  const unmountSideMenu = mountSideMenu(container);
  container.addEventListener('click', onClick);
  container.addEventListener('input', onInput);
  container.addEventListener('focusout', onFocusOut);

  void loadAll();

  // Ticks once a second: the reset clock, and the moment the window ends the rotation is re-read.
  const offerTick = window.setInterval(() => {
    if (disposed || !offersData) return;
    const left = new Date(offersData.windowEndsAt).getTime() - (Date.now() - serverSkewMs);
    if (left <= 0) {
      void refreshOffers().then(() => { if (!disposed) render(); });
      return;
    }
    const el = container.querySelector('[data-offers-reset]');
    if (el) el.textContent = t('offers.resetsIn', { time: formatClock(left) });
  }, 1000);

  return () => {
    disposed = true;
    window.clearInterval(offerTick);
    methodPickerHandle?.destroy();
    methodPickerHandle = null;
    panelHandle?.destroy();
    panelHandle = null;
    unmountSideMenu();
    container.removeEventListener('click', onClick);
    container.removeEventListener('input', onInput);
    container.removeEventListener('focusout', onFocusOut);
  };
}

export const shopTab: GameTab = {
  id: 'shop',
  label: t('nav.shop'),
  icon: 'shop',
  mount: renderShopScreen,
};
